import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { InvestorSectionId } from './investor-sections';
import { marketModelLabel } from './investor-field-labels';

const S = UI_STRINGS.INVESTOR_PROFILE;

/**
 * The readiness keys the Backend reports in `matchingMissingFields` (MYN-278, FR-186).
 * Mirrors the Backend's `INVESTOR_MATCHING_READINESS_FIELDS`; the list of which are
 * still missing always comes from the response, never from this app.
 */
export const INVESTOR_READINESS_KEYS = [
  'investorType',
  'ticketSizeLow',
  'ticketSizeHigh',
  'minimumRevenueRequirement',
  'recurringRevenuePreference',
  'preferredFundingStages',
  'femaleLedStartups',
  'underrepresentedFounders',
  'b2bPreference',
  'b2cPreference',
  'b2gPreference',
  'saasPreference',
  'aiPreference',
  'leadOrFollow',
  'preferredIndustries',
  'geographyPreferences',
] as const;

export type InvestorReadinessKey = (typeof INVESTOR_READINESS_KEYS)[number];

/**
 * On-screen name per readiness key, as the field is labelled on the profile's forms.
 * Mirrors the Backend's `INVESTOR_READINESS_KEY_LABELS`; change both together.
 */
export const INVESTOR_READINESS_KEY_LABELS: Record<InvestorReadinessKey, string> = {
  investorType: S.INVESTOR_TYPE,
  ticketSizeLow: S.MIN_INVESTMENT,
  ticketSizeHigh: S.MAX_INVESTMENT,
  minimumRevenueRequirement: S.MIN_REVENUE,
  recurringRevenuePreference: S.RECURRING_REVENUE,
  preferredFundingStages: S.PREFERRED_STAGES,
  femaleLedStartups: S.FEMALE_LED_STARTUPS,
  underrepresentedFounders: S.UNDERREPRESENTED_FOUNDERS,
  b2bPreference: marketModelLabel('prefB2b'),
  b2cPreference: marketModelLabel('prefB2c'),
  b2gPreference: marketModelLabel('prefB2g'),
  saasPreference: marketModelLabel('prefSaas'),
  aiPreference: marketModelLabel('prefAi'),
  leadOrFollow: S.LEAD_OR_FOLLOW,
  preferredIndustries: S.INDUSTRIES,
  geographyPreferences: S.GEOGRAPHIES,
};

/** The panel that holds each readiness key's field. Exhaustive: a new key fails the build until mapped. */
export const INVESTOR_READINESS_KEY_SECTION: Record<InvestorReadinessKey, InvestorSectionId> = {
  investorType: 'about',
  ticketSizeLow: 'preferences',
  ticketSizeHigh: 'preferences',
  minimumRevenueRequirement: 'preferences',
  recurringRevenuePreference: 'preferences',
  preferredFundingStages: 'preferences',
  femaleLedStartups: 'founder',
  underrepresentedFounders: 'founder',
  b2bPreference: 'preferences',
  b2cPreference: 'preferences',
  b2gPreference: 'preferences',
  saasPreference: 'preferences',
  aiPreference: 'preferences',
  leadOrFollow: 'preferences',
  preferredIndustries: 'preferences',
  geographyPreferences: 'geographies',
};

/** A key the Backend reports that this app knows how to name and open. */
export function isInvestorReadinessKey(key: string): key is InvestorReadinessKey {
  return Object.prototype.hasOwnProperty.call(INVESTOR_READINESS_KEY_LABELS, key);
}
